import React, { useEffect, useState } from 'react';
import { Box, Stepper, Step, StepLabel, StepConnector, Typography } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';

import { 
  DesignServicesIcon,
  Inventory2Icon,
  PrecisionManufacturingIcon,
  FactoryIcon,
  FactCheckIcon,
  LocalShippingIcon,
  TaskAltIcon,
  ReceiptLongIcon,
  CheckCircleIcon,
  VerifiedIcon
} from 'assets/constants/Icons';
import { BOM_STAGES } from 'assets/constants/Constants';

const ACTIVE_COLOR = '#2d3d42';
const DARK_COLOR = '#bbc2c4ff';
const INACTIVE_LINE_COLOR = alpha(DARK_COLOR, 0.7);

const STAGES_ORDER = [
  { key: BOM_STAGES.DESIGN, label: BOM_STAGES.DESIGN, icon: <DesignServicesIcon /> },
  { key: BOM_STAGES.PROCUREMENT, label: BOM_STAGES.PROCUREMENT, icon: <Inventory2Icon /> },
  { key: BOM_STAGES.MANUFACTURING_PLANNING, label: BOM_STAGES.MANUFACTURING_PLANNING, icon: <PrecisionManufacturingIcon /> },
  { key: BOM_STAGES.MANUFACTURING_EXECUTION, label: BOM_STAGES.MANUFACTURING_EXECUTION, icon: <FactoryIcon /> },
  { key: BOM_STAGES.TESTING, label: BOM_STAGES.TESTING, icon: <FactCheckIcon /> },
  { key: BOM_STAGES.DISPATCH, label: BOM_STAGES.DISPATCH, icon: <LocalShippingIcon /> },
  { key: BOM_STAGES.COMMERCIAL_CLEARANCE, label: BOM_STAGES.COMMERCIAL_CLEARANCE, icon: <ReceiptLongIcon /> },
  { key: BOM_STAGES.HANDOVER_TO_DISPATCH, label: BOM_STAGES.HANDOVER_TO_DISPATCH, icon: <TaskAltIcon /> },
  { key: BOM_STAGES.COMPLETED, label: BOM_STAGES.COMPLETED, icon: <CheckCircleIcon /> },
  { key: BOM_STAGES.VALIDATION, label: BOM_STAGES.VALIDATION, icon: <VerifiedIcon /> },
];


const MinimalConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    height: 2,  
    border: 0,
    backgroundColor: INACTIVE_LINE_COLOR,
    borderRadius: 1,
    transition: 'background-color 0.6s ease-in-out',
  },
  '&.Mui-active .MuiStepConnector-line': {
    backgroundColor: ACTIVE_COLOR,
  },
  '&.Mui-completed .MuiStepConnector-line': {
    backgroundColor: ACTIVE_COLOR,
  },
}));

const StyledStepLabel = styled(StepLabel)(({ theme }) => ({
  '& .MuiStepLabel-label': {
    color: alpha(DARK_COLOR, 0.7), 
    fontWeight: 500,
    fontSize: '0.75rem', 
    marginTop: '8px !important', 
    transition: 'color 0.3s ease',
    '&.Mui-active': {
      color: DARK_COLOR, 
      fontWeight: 700,
    },
    '&.Mui-completed': {
      color: ACTIVE_COLOR, 
      fontWeight: 500,
    },
  },

  '& .MuiStepLabel-iconContainer': {
    color: alpha(DARK_COLOR, 0.3), 
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
    padding: 4, 
    borderRadius: '50%',

    '&.Mui-active': {
        color: ACTIVE_COLOR,
        transform: 'scale(1.3)', 
    },

    '&.Mui-completed': {
      color: ACTIVE_COLOR,
      transform: 'scale(1.0)', 
    },
    
    '& svg': {
        fontSize: 26,
    }
  },
}));

// Helper to format date
const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    
    return new Intl.DateTimeFormat('en-GB', { 
        day: 'numeric', 
        month: 'short',
        year: '2-digit' 
    }).format(date);
};

const BomProgressBar = ({ currentStage, stageTimelines }) => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const index = STAGES_ORDER.findIndex((s) => s.key === currentStage);
    
    if (index !== -1) {
      let step = 0;
      const interval = setInterval(() => {
        if (step <= index) {
          setActiveStep(step);
          step++;
        } else {
          clearInterval(interval);
        }
      }, 150);
      return () => clearInterval(interval);
    } else {
      setActiveStep(0);
    }
  }, [currentStage]);

  return (
    <Box sx={{ width: '100%', mb: 3, mt: 3 }}>
      <Stepper 
        alternativeLabel 
        activeStep={activeStep} 
        connector={<MinimalConnector />}
      >
        {STAGES_ORDER.map((stage) => {
            const timelineData = stageTimelines ? stageTimelines[stage.key] : null;
            const completionDate = timelineData?.completion_date ? formatDate(timelineData.completion_date) : null;

            return (
              <Step key={stage.key}>
                <StyledStepLabel icon={stage.icon}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2 }}>
                        
                        <Box sx={{ 
                            minHeight: '2.5em', 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            justifyContent: 'center',
                            textAlign: 'center',
                            mb: 0.5 
                        }}>
                           {stage.label}
                        </Box>

                        <Typography 
                            variant="caption" 
                            sx={{ 
                                fontSize: '0.65rem', 
                                color: '#666 !important',
                                fontWeight: 600,
                                height: '1em' 
                            }}
                        >
                            {completionDate || ''}
                        </Typography>
                    </Box>
                </StyledStepLabel>
              </Step>
            );
        })}
      </Stepper>
    </Box>
  );
};

export default BomProgressBar;